"use client";

import { Product } from "lib/shopify/types";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { createUrl, mergeSearchParams } from "@/lib/utils";
import { useQueryState, parseAsJson } from "next-usequerystate";

// TODO: use this hook in VariantSelector component
// TODO: use the useQueryState hook to update the url params

export const useProductSearchParams = ({ handle }) => {
  const parser = parseAsJson().withDefault({});
  const [params, setParams] = useQueryState(handle, parser);

  return {
    searchParams: new URLSearchParams(params),
    setSearchParams: (urlParams) => setParams(Object.fromEntries(urlParams)),
    createUrl: (pathname, urlParams) => {
      const qs = parser.serialize(Object.fromEntries(urlParams));
      const nextParams = { [handle]: qs };
      const nextUrl = createUrl(pathname, new URLSearchParams(nextParams));

      // console.log({qs, nextUrl, nextParams})

      return nextUrl;
    }
  };
};
export const useOptionState = ({ options, variants, handle } = {}) => {
  const router = useRouter();

  const { searchParams, setSearchParams } = useProductSearchParams({ handle });
  const pathname = usePathname();

  if (!variants?.length || !options?.length) {
    return {
      options: [],
      variants: [],
      activeVariant: null
    };
  }

  const variantOptions = (variant) =>
    variant.selectedOptions?.reduce(
      (accumulator, option) => ({ ...accumulator, [option.name.toLowerCase()]: option.value }),
      {}
    ) || {};

  const combinations = variants.map((variant) => ({
    id: variant.id,
    availableForSale: variant.availableForSale,
    // Adds key / value pairs for each variant (ie. "color": "Black" and "size": 'M").
    ...variantOptions(variant)
  }));

  const optionsWithState = options.map((option) => {
    return {
      ...option,
      state: option.values.map((value) => {
        const optionNameLowerCase = option.name.toLowerCase();

        // Base option params on current params so we can preserve any other param state in the url.
        const optionSearchParams = new URLSearchParams(searchParams.toString());

        // Update the option params using the current option to reflect how the url *would* change,
        // if the option was clicked.
        optionSearchParams.set(optionNameLowerCase, value);

        // In order to determine if an option is available for sale, we need to:
        //
        // 1. Filter out all other param state
        // 2. Filter out invalid options
        // 3. Check if the option combination is available for sale
        //
        // This is the "magic" that will cross check possible variant combinations and preemptively
        // disable combinations that are not available. For example, if the color gray is only available in size medium,
        // then all other sizes should be disabled.
        const filtered = Array.from(optionSearchParams.entries()).filter(([key, value]) =>
          options.find(
            (option) => option.name.toLowerCase() === key && option.values.includes(value)
          )
        );
        const isAvailableForSale = combinations.find((combination) =>
          filtered.every(
            ([key, value]) => combination[key] === value && combination.availableForSale
          )
        );

        // The option is active if it's in the url params.
        const isActive = searchParams.get(optionNameLowerCase) === value;

        return {
          isActive,
          isAvailableForSale,
          activate: () => {
            setSearchParams(optionSearchParams);
          },
          name: optionNameLowerCase,
          value
        };
      })
    };
  });

  const activeOptions = optionsWithState
    .map(({ state }) => state.find(({ isActive }) => isActive))
    .filter(Boolean);

  const matchOpt = (a, b) => a.name.toLowerCase() === b.name.toLowerCase() && a.value === b.value;

  const variantsWithState = variants.map((variant) => {
    const includesOpt = (opt) =>
      variant.selectedOptions.find((selectedOpt) => matchOpt(selectedOpt, opt));

    const isActive = activeOptions.length && activeOptions.every(includesOpt);
    const variantSearchParams = new URLSearchParams(searchParams.toString());
    variant.selectedOptions?.forEach((option) => {
      variantSearchParams.set(option.name.toLowerCase(), option.value);
    });

    return {
      ...variant,
      isActive,
      activate: () => {
        setSearchParams(variantSearchParams);
      }
    };
  });

  const activeVariant = variantsWithState.find((variant) => variant.isActive);
  const defaultVariant = variantsWithState[0];

  return {
    options: optionsWithState,
    variants: variantsWithState,
    activeOptions,
    defaultVariant,
    activeVariant
  };
};
